import { useMap } from "@vis.gl/react-google-maps";
import {
    usePilotCertificateCoastalSegmentPerson,
    useSetSelectedCoastalSegmentGroup,
    useSetSelectedLocation,
    useSetShowInfo,
    useSetExcludeLocationIds,
    useSetShowLocations,
    useSetCoastalSegmentGroups,
    useSetShowLocationQuayManual,
    useShowLocations,
    usePilotCertificateToggleFeatures,
    useSelectedPilotCoastalSegmentGroup
} from "../hooks/useMapStore";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import MapApiRepository from "../repositories/MapApiRepository";
import { MapPolygon } from "./MapPolygon";
import MapService, { IMapService } from "../services/MapService";
import { MapCertificateLocationMarker } from "./markers/MapCertificateLocationMarker";
import MapPropertiesService from "../services/MapPropertiesService";
import { useSetIsBusy, useApplicationRights } from "components/hooks/useAppStore"; 
import { ApplicationRightService } from "../../../../services/ApplicationRightService";
import { ApplicationRightSystemNames } from "../../../constants";
import { AdminWriteRight as PilotCertificateCoastalSegmentsAdminWriteRight  } from "@pilot-web/pilot-certificate-coastal-segments";

export const MapPilotCertificateCoastalSegment = () => {

    const map = useMap();

    const setIsBusy = useSetIsBusy();
    const applicationRights = useApplicationRights();

    const pilotCertificateCoastalSegmentPerson = usePilotCertificateCoastalSegmentPerson();
    const setSelectedCoastalSegmentGroup = useSetSelectedCoastalSegmentGroup();
    const setSelectedLocation = useSetSelectedLocation();
    const setShowInfo = useSetShowInfo();
    const setExcludeLocationIds = useSetExcludeLocationIds();
    const setShowLocations = useSetShowLocations();
    const setCoastalSegmentGroups = useSetCoastalSegmentGroups();
    const setShowLocationQuayManual = useSetShowLocationQuayManual();
    const showLocations = useShowLocations();
    const pilotCertificateToggleFeatures = usePilotCertificateToggleFeatures();
    const selectedPilotCoastalSegmentGroup = useSelectedPilotCoastalSegmentGroup();
    
    const [sourceFeatures, setSourceFeatures] = useState<any[]>([]);
    const [features, setFeatures] = useState<any[]>([]);
    const [availableFeatures, setAvailableFeatures] = useState<any[]>([]);
    const [markers, setMarkers] = useState<any[]>([]);

    const mapService: IMapService = useMemo(() => new MapService(), []);
    
    const groupToFeature = useCallback((group: any): any => {
        const feature = {
            id: group.id,
            type: "Feature",
            geometry: null,
            properties: {
                ascii: 1,
                segmentName: group.name,
                wkt: group.geometryWKT,
                featurePropertyType: "GROUP",
                featureType: "CertificateGroupAreaFeatureType",
                isSelected: group.isSelected,
                status: group.status
            }
        };
        mapService.setFeatureGeometryAndBounds(feature);
        return feature;
    }, [mapService]);

    const fetchCoastalSegmentGroups = async (certificateData:any[]) => {
        const response = await MapApiRepository.searchPilotCoastalSegmentGroupsGeometriesAsync();
        if (!response.ok) return;

        const data: any = await response.json();
        const currentIds = certificateData.map(f => f.id);
        const availableFeatures = data.features.filter((f: any) => !currentIds.includes(f.id));

        for (const feature of availableFeatures) {
            mapService.setFeatureGeometryAndBounds(feature);
            feature.properties.featureType = "AvailableFeatureType";
            feature.properties.isSelected = false;
        }
        setAvailableFeatures(availableFeatures);
    }

    const thenFetchData = (data:any[]) => {

        const coastalSegmentGroups = data.map((group: any) => ({
            id: group.id,
            name: group.name,
            isSelected: group.isSelected,
            status: group.status
        }));
        setCoastalSegmentGroups(coastalSegmentGroups);

        if (pilotCertificateCoastalSegmentPerson?.mode === "APPROVE") return;

        const locations: any[] = [];
        
        for (const group of data) {
            for (const location of group.locations) {
                const latLng = new window.google.maps.LatLng(location.latitude, location.longitude);
                locations.push({
                    type: "Location",
                    position: latLng,
                    description: location.name,
                    id: location.id,
                    isEligibleForSpecialPilotageFromTimeLockDeadline: location.isEligibleForSpecialPilotageFromTimeLockDeadline,
                    color: getLocationColor(location.locationType),
                });
            }
        }

        setMarkers(locations);

        if(locations.length === 0) return;

        const excludeLocationIds = locations.map(loc => loc.id);
        setExcludeLocationIds(excludeLocationIds);

        setShowLocations(true);
    }

    const fetchData = async (response:Response) => {
        if (response.ok) {
            const data = await response.json();
            const groupFeatures: any[] = [];

            for (const group of data) {
                const feature = groupToFeature(group);

                groupFeatures.push(feature);
            }

            setSourceFeatures(groupFeatures);

            thenFetchData(data);

            if (pilotCertificateCoastalSegmentPerson?.mode !== "APPROVE") {
                fetchCoastalSegmentGroups(data);
            }
        }

        setIsBusy(false);
    }

    const clearData = useCallback(() => {
        setFeatures([]);
        setSourceFeatures([]);
        setMarkers([]);
        setSelectedCoastalSegmentGroup(null);
        setSelectedLocation(null);
        setShowInfo(null);
        setAvailableFeatures([]);
        setExcludeLocationIds([]);
        setShowLocations(false);
    }, [setFeatures,
        setSourceFeatures,
        setMarkers,
        setSelectedCoastalSegmentGroup,
        setSelectedLocation,
        setShowInfo,
        setAvailableFeatures,
        setExcludeLocationIds,
        setShowLocations]);

    useEffect(() => {

        if (!map || !applicationRights || applicationRights.length === 0) return;

        const initialize = async () => {

            setSelectedCoastalSegmentGroup(null);
            setSelectedLocation(null);
            setShowInfo(null);
            setShowLocationQuayManual(false);

            setIsBusy(true);

            const applicationRightsService = new ApplicationRightService(applicationRights);
            const isAdmin = applicationRightsService.canAccess([PilotCertificateCoastalSegmentsAdminWriteRight]);

            const response = isAdmin ?
                await MapApiRepository.getPilotCertificateCoastalSegmentGroupsByIdAsync(pilotCertificateCoastalSegmentPerson.id) :
                await MapApiRepository.getPilotCertificateCoastalSegmentGroupsAsync();

            fetchData(response);
        }

        clearData();

        if (!pilotCertificateCoastalSegmentPerson) return;

        initialize();

    }, [map, pilotCertificateCoastalSegmentPerson, applicationRights]);

    useEffect(() => {
        return () => {
            clearData();
        }
    }, []);

    useEffect(() => {
        if(sourceFeatures.length === 0) return;

        let filteredFeatures: any[] = [];
        if(pilotCertificateToggleFeatures) {
            filteredFeatures = sourceFeatures.filter(f => f.properties.status === "READY_FOR_APPROVAL");
        } else {
            filteredFeatures = sourceFeatures;
        }

        setFeatures(filteredFeatures);
        
        if(filteredFeatures.length === 0) return;
        
        const bounds = new window.google.maps.LatLngBounds();
        for (const feature of filteredFeatures) {
            const coordinates = mapService.getCoordinatesFromGeometry(feature.geometry);
            mapService.extendBounds(bounds, coordinates);
        }
        map!.setCenter(bounds.getCenter());
        map!.fitBounds(bounds);

    }, [sourceFeatures, pilotCertificateToggleFeatures]);

    useEffect(() => {
        if (!selectedPilotCoastalSegmentGroup) return;

        const feature = features.find(f => f.id === selectedPilotCoastalSegmentGroup.id);
        if(!feature) return;

        const bounds = new window.google.maps.LatLngBounds();
        const coordinates = mapService.getCoordinatesFromGeometry(feature.geometry);
        mapService.extendBounds(bounds, coordinates);
        
        map!.setCenter(bounds.getCenter());
        map!.fitBounds(bounds);

    }, [selectedPilotCoastalSegmentGroup]);

    return (
        <>
            {features.map((feature: any, index: number) => (
                <MapPolygon key={feature.id ?? index} source={feature} />
            ))}

            {
                showLocations &&
                    markers.map((marker, index) => (
                    <MapCertificateLocationMarker key={marker.id ?? index} marker={marker} />
                ))
            }

            {availableFeatures.map((feature: any, index: number) => (
                <MapPolygon key={feature.id ?? index} source={feature} />
            ))}
        </>
    )
}

const getLocationColor = (systemName: string) => {
    const locationTypes = MapPropertiesService.locationTypes;
    switch (systemName) {
        case locationTypes.Harbour:
            return "var(--map-pin-location-harbour-bg)";
        case locationTypes.Anchorage:
            return "var(--map-pin-location-anchorage-bg)";
        case locationTypes.LocationAtSea:
            return "var(--map-pin-location-location-at-sea-bg)";
        case locationTypes.PilotBoarding:
            return "var(--map-pin-location-pilot-boarding-bg)";
        default:
            return "var(--map-pin-location-default-bg)";
    }
}
